import {
  Coins,
  DoorOpen,
  FileBadge,
  FileSignature,
  FileSpreadsheet,
  ListPlus,
  Plane,
  Play,
  Receipt,
  ReceiptText,
  ScrollText,
  Settings2,
  Undo2,
  UserRoundSearch,
  Wallet,
} from "lucide-react";
import { ROUTES } from "@/app/routes";
import { Permission } from "@/shared/auth/permissions";
import type { NavItem, NavSection } from "@/shared/components/layout/sidebar-nav.types";

const payroll: NavItem = {
  to: "",
  label: "Payroll",
  icon: ReceiptText,
  moduleId: "payroll",
  children: [
    { to: "", label: "This month", icon: ReceiptText, heading: true },
    {
      to: ROUTES.workspace.payrollRuns.path,
      label: "Run payroll",
      icon: Play,
      requires: Permission.PAYROLL_RUN_VIEW_WORKSPACE,
      requiresFeature: "payroll",
    },
    {
      to: ROUTES.workspace.payrollInputs.path,
      label: "This month's extras",
      icon: ListPlus,
      requires: Permission.ADDITIONAL_SALARY_MANAGE_WORKSPACE,
      requiresFeature: "payroll",
    },
    {
      to: ROUTES.workspace.recoverySchedules.path,
      label: "Recoveries",
      icon: Undo2,
      requires: Permission.PAYROLL_RUN_VIEW_WORKSPACE,
      requiresFeature: "payroll",
    },
    { to: "", label: "People pay", icon: Coins, heading: true },
    {
      to: ROUTES.workspace.payrollSalaries.path,
      label: "Salaries",
      icon: Coins,
      requires: Permission.SALARY_ASSIGNMENT_MANAGE_WORKSPACE,
      requiresFeature: "payroll",
    },
    {
      to: ROUTES.workspace.variablePay.path,
      label: "Variable pay",
      icon: Coins,
      requires: Permission.VARIABLE_PAY_MANAGE_WORKSPACE,
      requiresFeature: "payroll",
    },
    { to: "", label: "Exits", icon: DoorOpen, heading: true },
    {
      to: ROUTES.workspace.settlements.path,
      label: "Settlements",
      icon: DoorOpen,
      requires: Permission.FNF_MANAGE_WORKSPACE,
      requiresFeature: "payroll",
    },
    { to: "", label: "Year-end", icon: FileBadge, heading: true },
    {
      to: ROUTES.workspace.complianceDocuments.path,
      label: "Compliance documents",
      icon: FileBadge,
      requires: Permission.COMPLIANCE_DOCUMENT_MANAGE_WORKSPACE,
      requiresFeature: "payroll",
    },
    { to: "", label: "Audit", icon: ScrollText, heading: true },
    {
      to: ROUTES.workspace.payrollMoneyAudit.path,
      label: "Money audit",
      icon: ScrollText,
      requires: Permission.SALARY_SLIP_VIEW_WORKSPACE,
      requiresFeature: "payroll",
    },
    {
      to: ROUTES.workspace.parallelRun.path,
      label: "Parallel run",
      icon: FileSpreadsheet,
      requires: Permission.PAYROLL_RUN_MANAGE_WORKSPACE,
      requiresFeature: "payroll",
    },
    { to: "", label: "Setup", icon: Settings2, heading: true },
    {
      to: ROUTES.workspace.payrollSetup.path,
      label: "Setup",
      icon: Settings2,
      requires: Permission.PAYROLL_CONFIG_VIEW,
      requiresFeature: "payroll",
      neverLanding: true,
    },
  ],
};

const expenses: NavItem = {
  to: "",
  label: "Expenses",
  icon: Receipt,
  moduleId: "expenses",
  children: [
    {
      to: ROUTES.workspace.expenses.path,
      label: "Claims",
      icon: Receipt,
      requires: Permission.EXPENSE_CLAIM_VIEW_OWN,
      requiresFeature: "expenses",
    },
    {
      to: ROUTES.workspace.expenseAdvances.path,
      label: "Advances",
      icon: Wallet,
      requires: Permission.ADVANCE_VIEW_OWN,
      requiresFeature: "expenses",
    },
    {
      to: ROUTES.workspace.expenseTravel.path,
      label: "Travel",
      icon: Plane,
      requires: Permission.TRAVEL_REQUEST_VIEW_OWN,
      requiresFeature: "expenses",
    },
  ],
};

const hiring: NavItem = {
  to: "",
  label: "Hiring",
  icon: UserRoundSearch,
  moduleId: "hiring",
  children: [
    {
      to: ROUTES.workspace.recruitment.path,
      label: "Jobs",
      icon: UserRoundSearch,
      requires: Permission.JOB_REQUISITION_CREATE,
      requiresFeature: "recruitment",
    },
    {
      to: ROUTES.workspace.recruitmentInterviews.path,
      label: "My interviews",
      icon: UserRoundSearch,
      requires: Permission.INTERVIEW_FEEDBACK_SUBMIT_OWN,
      requiresFeature: "recruitment",
    },
    {
      to: ROUTES.workspace.recruitmentOffers.path,
      label: "Offers",
      icon: FileSignature,
      requires: Permission.JOB_OFFER_VIEW_WORKSPACE,
      requiresFeature: "recruitment",
    },
  ],
};

export const COMPANY_SECTION: NavSection = {
  title: "Company",
  items: [
    payroll,
    expenses,
    hiring,
    {
      to: ROUTES.workspace.compensation.path,
      label: "Compensation",
      beta: true,
      icon: Coins,
      requires: Permission.COMP_LETTER_VIEW_OWN,
      requiresFeature: "compensation",
    },
  ],
};
